import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { addMonths, format, startOfMonth, subMonths } from 'date-fns'
import { ChevronLeft, ChevronRight, ExternalLink, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ErrorState } from '@/components/ui/error-state'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import {
  CALENDAR_COLOR_CLASSES,
  MonthGrid,
  toCalendarDate,
  type CalendarReservation,
} from '@/components/calendar/month-grid'
import { STAY_FOCUS_RING, buildStayBars } from '@/components/calendar/stay-bars'
import { ReservationSidebar } from '@/components/messages/reservation-sidebar'
import { usePropertyReservations, useReservation } from '@/hooks/useApi'
import { cn } from '@/lib/utils'

/**
 * The stay behind a cleaning, opened from the board's Stay cell (CAP-518),
 * drawn inside the property's month.
 *
 * The board knows a cleaning follows a reservation - it prints the stay's
 * dates - and then stops, so the questions the dates raise (who is it, which
 * channel, is it still confirmed, what is the door code) were answered by
 * searching the reservation up in another tab, or not answered at all.
 *
 * One stay on its own answers those and raises the next: what is around it.
 * A three-night stay reads differently with a back-to-back arrival the same
 * day than with the house empty for a week, and the operator opening a stay
 * from the cleaning board is nearly always about to ask that. So the whole
 * month of stays is drawn beside the summary, the one the row named ringed,
 * and any other stay in the month opens in the same panel on a click.
 *
 * Read only, like the job calendar beside it: everything that changes a
 * cleaning stays on the row and in the job drawer, and everything that changes
 * a RESERVATION belongs on the reservation page, which the footer links to.
 *
 * Reuses the inbox's `ReservationSidebar` rather than restating its fields -
 * it already renders exactly this summary, with every absent value guarded.
 */
interface ReservationDialogProps {
  /** `source.id` of a `reservation`-sourced job. */
  reservationId: string
  /** The property the job belongs to, whose month is drawn. */
  propertyId: string
  /** The property the board row named, so the dialog opens under it. */
  propertyName: string
  /**
   * The stay's check-in day in the property's zone (`YYYY-MM-DD`), which
   * decides the month opened first. Null falls back to the current month.
   */
  stayDay: string | null
  onClose: () => void
}

export function CleaningReservationDialog({
  reservationId,
  propertyId,
  propertyName,
  stayDay,
  onClose,
}: ReservationDialogProps) {
  const [monthCursor, setMonthCursor] = useState(() =>
    startOfMonth(stayDay ? toCalendarDate(stayDay) : new Date())
  )
  /**
   * The stay the panel shows. Starts on the one the row named; clicking
   * another bar in the month swaps it, and the ring follows.
   */
  const [selectedId, setSelectedId] = useState(reservationId)

  // Shares the properties page's cache key, so opening this on a property
  // already looked at costs nothing.
  const reservationsQuery = usePropertyReservations(propertyId)
  const reservationQuery = useReservation(selectedId)

  const bars = useMemo(
    () =>
      buildStayBars((reservationsQuery.data?.reservations ?? []) as CalendarReservation[], {
        focusedId: selectedId,
        onSelect: setSelectedId,
      }),
    [reservationsQuery.data, selectedId]
  )

  const isRowStay = selectedId === reservationId

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="top-8 max-h-[calc(100vh-4rem)] translate-y-0 max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-left">{propertyName}</DialogTitle>
        </DialogHeader>

        <p className="-mt-2 text-sm text-textMuted">
          The month around the stay this cleaning follows. Read only - change a stay on its
          reservation.
        </p>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-medium text-text">
                {format(monthCursor, 'MMMM yyyy')}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Previous month"
                  onClick={() => setMonthCursor(prev => subMonths(prev, 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Next month"
                  onClick={() => setMonthCursor(prev => addMonths(prev, 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {reservationsQuery.isError ? (
              // A failed read is named rather than drawn as an empty month: a
              // calendar that silently omits the stays is worse than none,
              // because seeing them is the whole point of opening it.
              <ErrorState
                error={reservationsQuery.error as Error}
                onRetry={() => void reservationsQuery.refetch()}
              />
            ) : reservationsQuery.isLoading ? (
              <div className="space-y-2">
                <LoadingSkeleton className="h-8 w-full" />
                <LoadingSkeleton className="h-56 w-full" />
              </div>
            ) : (
              <>
                <MonthGrid monthCursor={monthCursor} bars={bars} />
                <div className="flex flex-wrap items-center gap-3 text-xs text-textMuted">
                  {/* The stay colours are the property calendar's, not a
                      second scheme: the same bar should not mean Airbnb on one
                      screen and "a stay" on another. */}
                  <LegendSwatch className={CALENDAR_COLOR_CLASSES.airbnb}>Airbnb</LegendSwatch>
                  <LegendSwatch className={CALENDAR_COLOR_CLASSES.vrbo}>VRBO</LegendSwatch>
                  <LegendSwatch className={CALENDAR_COLOR_CLASSES.direct}>Direct</LegendSwatch>
                  <LegendSwatch className={CALENDAR_COLOR_CLASSES.pending}>Pending</LegendSwatch>
                  <LegendSwatch className={cn(CALENDAR_COLOR_CLASSES.unknown, STAY_FOCUS_RING)}>
                    Selected
                  </LegendSwatch>
                </div>
              </>
            )}
          </div>

          <div className="min-w-0 space-y-3" data-testid="stay-panel">
            <div className="flex min-h-8 items-center justify-between gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-textMuted">
                {isRowStay ? "This cleaning's stay" : 'Another stay this month'}
              </p>
              {!isRowStay && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Back to this cleaning's stay"
                  onClick={() => setSelectedId(reservationId)}
                >
                  <Undo2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  Back
                </Button>
              )}
            </div>

            {reservationQuery.isLoading ? (
              <div className="space-y-3">
                <LoadingSkeleton className="h-6 w-2/3" />
                <LoadingSkeleton className="h-40 w-full" />
              </div>
            ) : reservationQuery.isError || !reservationQuery.data ? (
              <ErrorState
                error={reservationQuery.error as Error}
                onRetry={() => reservationQuery.refetch()}
              />
            ) : (
              <>
                {/* `external` is `unknown` on the contract's reservation
                    schema - the KV record is a long-lived blob several
                    pipelines write, so it is deliberately not modelled as a
                    closed shape. Same local narrowing every other reader of
                    this field does. */}
                <ReservationSidebar
                  reservation={
                    reservationQuery.data as Parameters<typeof ReservationSidebar>[0]['reservation']
                  }
                />
                <div className="flex justify-end">
                  <Button variant="secondary" size="sm" asChild>
                    <Link to={`/reservations/${encodeURIComponent(selectedId)}`}>
                      Open reservation
                      <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                    </Link>
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function LegendSwatch({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn('h-2.5 w-2.5 rounded-sm border', className)} aria-hidden="true" />
      {children}
    </span>
  )
}
